import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/planners", label: "For Planners" },
    { to: "/inquiry", label: "Inquire" },
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-[#0F0F0F]/95 backdrop-blur-sm py-4"
            : "bg-transparent py-6"
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="font-serif text-xl tracking-widest text-[#F4F2EE] hover:text-[#B8A369] transition-colors duration-300"
            data-testid="logo-link"
          >
            <span className="font-light">REFINED</span>
            <span className="text-[#B8A369] mx-1">·</span>
            <span className="font-light">SOUND</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${
                  location.pathname === link.to ? "text-[#B8A369]" : ""
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#F4F2EE] p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
          data-testid="mobile-menu"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-2xl font-serif tracking-widest text-[#F4F2EE] hover:text-[#B8A369] transition-colors duration-300 animate-fade-in`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-16 px-6 lg:px-12" data-testid="main-footer">
        <div className="max-w-7xl mx-auto">
          <div className="line-gold mb-12" />
          
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Brand */}
            <div>
              <h3 className="font-serif text-xl tracking-widest text-[#F4F2EE] mb-4">
                <span className="font-light">REFINED</span>
                <span className="text-[#B8A369] mx-1">·</span>
                <span className="font-light">SOUND</span>
              </h3>
              <p className="text-sm text-[#F4F2EE]/50 leading-relaxed">
                Crafted live music for elevated gatherings.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-xs tracking-widest uppercase text-[#B8A369] mb-4">
                Navigation
              </h4>
              <div className="space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-sm text-[#F4F2EE]/60 hover:text-[#B8A369] transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs tracking-widest uppercase text-[#B8A369] mb-4">
                Inquiries
              </h4>
              <p className="text-sm text-[#F4F2EE]/60 leading-relaxed mb-4">
                For private event inquiries, please submit a request through our inquiry form.
              </p>
              <Link
                to="/inquiry"
                className="inline-block text-xs tracking-widest uppercase text-[#B8A369] hover:text-[#F4F2EE] transition-colors duration-300"
              >
                Request Availability
              </Link>
            </div>
          </div>

          <div className="line-gold mb-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center text-xs text-[#F4F2EE]/30 tracking-wide">
            <p>Private referrals preferred</p>
            <p className="mt-2 md:mt-0">Limited availability</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
